<template>
  <div>
    <div>
      <div
        v-tooltip="propertySchema.description ?? ''"
        class="check-property-label"
      >
        {{ propertyLabel }}
      </div>
      <PVInputText
        v-if="isStringProperty"
        v-model="simpleValue as string"
        :invalid="!inputState"
      />
      <PVSelect
        v-else-if="isEnumProperty"
        v-model="simpleValue"
        :options="possibleValues"
        :invalid="!inputState"
      />
      <PVInputNumber
        v-else-if="isNumberProperty"
        v-model="simpleValue as number"
        :invalid="!inputState"
        :mode="'decimal'"
        :max-fraction-digits="isDecimalProperty ? 20 : 0"
      />
      <PVCheckbox
        v-else-if="isBooleanProperty"
        v-model="simpleValue"
        binary
        class="check-box-input"
      />
      <styled-tags-input
        v-else-if="isArrayEnumProperty"
        v-model="arrayValue"
        :add-only-from-autocomplete="true"
        :autocomplete-items="possibleValues"
      />
      <styled-tags-input
        v-else-if="isFreeTextArrayProperty"
        v-model="arrayValue"
      />
      <message-wrapper v-if="!inputState" simple-error>{{
        invalidFeedback
      }}</message-wrapper>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { CheckProperty, KnownCheckBotValueType } from 'types';
  import StyledTagsInput from '@/components/StyledTagsInput/StyledTagsInput.vue';
  import { CheckBotConfigsService } from '@/services';
  import { useI18n } from 'vue-i18n';
  import PVInputNumber from 'primevue/inputnumber';
  import PVInputText from 'primevue/inputtext';

  const props = defineProps<{
    propertySchema: CheckProperty;
    modelValue: KnownCheckBotValueType | null;
  }>();

  const { t } = useI18n();

  const arrayValue = ref<Extract<KnownCheckBotValueType, Array<string>>>([]);
  const simpleValue = ref<Exclude<
    KnownCheckBotValueType,
    Array<string>
  > | null>(null);

  const isStringProperty = computed(() => {
    return (
      props.propertySchema.type === 'string' &&
      !props.propertySchema.possibleValues
    );
  });

  const isEnumProperty = computed(() => {
    return (
      props.propertySchema.type === 'string' &&
      !!props.propertySchema.possibleValues
    );
  });

  const isNumberProperty = computed(() => {
    return (
      props.propertySchema.type === 'number' ||
      props.propertySchema.type === 'integer'
    );
  });

  const isDecimalProperty = computed(() => {
    return props.propertySchema.type === 'number';
  });

  const isBooleanProperty = computed(() => {
    return props.propertySchema.type === 'boolean';
  });

  const isArrayProperty = computed(() => {
    return props.propertySchema.type === 'array';
  });

  const isFreeTextArrayProperty = computed(() => {
    return (
      props.propertySchema.type === 'array' &&
      !props.propertySchema.possibleValues
    );
  });

  const isArrayEnumProperty = computed(() => {
    return (
      props.propertySchema.type === 'array' &&
      !!props.propertySchema.possibleValues
    );
  });

  const possibleValues = computed(() => {
    if (
      !(
        props.propertySchema.type === 'string' ||
        props.propertySchema.type === 'array'
      )
    ) {
      throw new Error(
        'Possible values are only available for string and array properties',
      );
    }
    return props.propertySchema.possibleValues ?? [];
  });

  const propertyLabel = computed(() => {
    const name = props.propertySchema.name;
    if (props.propertySchema.type === 'array') {
      return props.propertySchema.minItems ? `${name} *` : name;
    }
    return props.propertySchema.isRequired ? `${name} *` : name;
  });

  const inputState = computed(() => {
    const { isValid } = CheckBotConfigsService.validateCheckProperty(
      props.propertySchema,
      isArrayProperty.value ? arrayValue.value : simpleValue.value,
    );
    return isValid;
  });

  const invalidFeedback = computed(() => {
    const { errorType } = CheckBotConfigsService.validateCheckProperty(
      props.propertySchema,
      isArrayProperty.value ? arrayValue.value : simpleValue.value,
    );
    if (!errorType) return '';
    return t(`checkPropertyEditor.invalidFeedback.${errorType}`, {
      ...(CheckBotConfigsService.isNumericTypeSchema(props.propertySchema)
        ? {
            min: props.propertySchema.min,
            max: props.propertySchema.max,
          }
        : {}),
      ...(CheckBotConfigsService.isArrayTypeSchema(props.propertySchema)
        ? {
            minItems: props.propertySchema.minItems,
            maxItems: props.propertySchema.maxItems,
          }
        : {}),
    }).toString();
  });

  const emit = defineEmits<{
    (e: 'update:modelValue', payload: KnownCheckBotValueType | null): void;
  }>();

  function setLocalValue(value: KnownCheckBotValueType | null) {
    if (props.propertySchema.type === 'array') {
      arrayValue.value =
        (value as string[]) ?? props.propertySchema.default ?? [];
    } else {
      simpleValue.value =
        (value as Exclude<KnownCheckBotValueType, Array<string>>) ??
        props.propertySchema.default;
    }
  }

  watch(
    () => props.modelValue,
    (value) => {
      setLocalValue(value);
    },
    { immediate: true },
  );

  watch(
    simpleValue,
    () => {
      emit('update:modelValue', simpleValue.value);
    },
    { immediate: true },
  );

  watch(
    arrayValue,
    () => {
      if (isArrayProperty.value) {
        emit('update:modelValue', arrayValue.value);
      }
    },
    { deep: true, immediate: true },
  );
</script>

<style lang="scss" scoped>
  .check-property-label {
    &:hover {
      cursor: help;
    }
  }
  .check-box-input {
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
